'use client';

/**
 * MyShellModal — full-screen My Shell overlay.
 *
 * Mounts the real MyShell (agent ACP client + terminal + tasks iframes) in a
 * viewport-covering overlay so root users can drive the agent without
 * navigating away — the same pattern as IamConsoleModal / ProfileModal /
 * MessagesModal. Root-gated: non-root renders null.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { Bot, ChevronLeft, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/contexts/ThemeContext';
import CapacitorModalFilterHeader, { capacitorHeaderActionClass } from '@/components/CapacitorModalFilterHeader';
import { MOBILE_SAFE_AREA_BOTTOM, MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';
import { haptics } from '@/lib/haptics';
import type { MyShellNavHandle } from '@/components/shell/MyShell';

const MyShell = dynamic(
  () => import('@/components/shell/MyShell'),
  { ssr: false },
);

// Keep-mounted after first open — closing hides instead of unmounting so
// the warm iframes (websockets, transcripts, composer drafts) survive an
// open/close cycle instead of cold-reloading every time. Module-level so
// no state flip is needed inside the component.
let myShellEverOpened = false;

export default function MyShellModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { isRoot } = useAuth();
  const { isDark } = useTheme();
  // Persona the shell is focused on — the header swaps the Shelly AI
  // identity for the persona's icon/name/tagline while one is open.
  const [activePersona, setActivePersona] = React.useState<{
    name: string; tagline?: string; icon: typeof Bot; color?: string;
  } | null>(null);
  // Any non-home shell view — drives the header's back-to-team button.
  const [shellDeep, setShellDeep] = React.useState(false);
  // Imperative handle into the mounted MyShell (go-home / reload) — the
  // shell assigns it; this header's buttons call it.
  const shellNavRef = React.useRef<MyShellNavHandle | null>(null);
  if (isOpen) myShellEverOpened = true;

  if (!myShellEverOpened || !isRoot || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[95] bg-gray-50 dark:bg-gray-900 overflow-hidden"
      data-tour-id="my-shell-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Shelly AI"
      style={{
        paddingTop: MOBILE_SAFE_AREA_TOP,
        paddingBottom: MOBILE_SAFE_AREA_BOTTOM,
        display: isOpen ? undefined : 'none',
      }}
    >
      <div className="flex h-full flex-col">
        <CapacitorModalFilterHeader
          icon={activePersona?.icon || Bot}
          iconColor={activePersona?.color}
          title={activePersona?.name || 'Shelly AI'}
          subtitle={activePersona?.tagline || 'Agentic Assistant Platform'}
          isDark={isDark}
          onClose={onClose}
          leadingActions={
            shellDeep ? (
              <button
                type="button"
                onClick={() => { void haptics.buttonTap(); shellNavRef.current?.goHome(); }}
                title="Back to team"
                aria-label="Back to team"
                className={capacitorHeaderActionClass(isDark)}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            ) : undefined
          }
          actions={
            shellDeep ? (
              <button
                type="button"
                onClick={() => { void haptics.buttonTap(); shellNavRef.current?.reload(); }}
                title="Reload"
                aria-label="Reload"
                className={capacitorHeaderActionClass(isDark)}
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            ) : undefined
          }
        />

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <MyShell
            onBack={onClose}
            fillParent
            headerless
            active={isOpen}
            navRef={shellNavRef}
            onActivePersona={(p, deep) => { setActivePersona(p); setShellDeep(!!deep); }}
          />
        </div>

      </div>
    </div>,
    document.body,
  );
}
